import {
  type PrivateFieldDef,
  type PrivateFormData,
  SIZE_MAX,
  SIZE_MIN,
  ZipCityPicker,
  fromPrivateFieldRows,
  getFieldOptions,
  getPrivateFieldOptions,
  privateFieldsForGender,
  toPrivatePayload,
} from '@/entities/profile';
import { useAuth } from '@/features/authentication';
import { profileApi, userApi } from '@/features/profile-management';
import { referenceApi } from '@/shared/api';
import { getStorageUrl } from '@/shared/lib';
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/ui/avatar';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { Checkbox } from '@/shared/ui/checkbox';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Skeleton } from '@/shared/ui/skeleton';
import { Textarea } from '@/shared/ui/textarea';
import { ImageCropper } from '@/widgets/image-cropper';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import {
  Baby,
  Briefcase,
  Camera,
  Car,
  CheckCircle2,
  Cigarette,
  Clock,
  Eye,
  Globe,
  GraduationCap,
  Heart,
  Home,
  Languages,
  Loader2,
  Lock,
  MapPin,
  Moon,
  Palette,
  Pencil,
  Phone,
  Ruler,
  Save,
  Scissors,
  Star,
  User,
  Users,
  X,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

// Form data interface
interface ProfileFormData {
  description: string;
  zipCode: string;
  /** town picked for the zip; undefined = keep the current one */
  geoId?: number;
  cityLabel: string;
  profession: string;
  avatarId?: number;
  studyLevel?: number;
  nodeBusiness?: number;
  isStudyLevelMatter?: number;
  maritalStatus?: number;
  children?: number;
  livingPlace?: number;
  licence?: number;
  smoke?: number;
  faith?: number;
  minaj?: number;
  veil?: number;
  familyValues?: number;
  peerPast?: number;
  administrativeState?: number;
  nationality?: number;
  origin?: number;
  studyCountry?: number;
  spokenLanguages: string[];
  lifePersonality: string[];
  shape?: number;
  size?: number;
  eye?: number;
  hair?: number;
}

// Helper component to display a profile field (view mode)
function ProfileField({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ElementType;
  label: string;
  value: string | undefined | null;
}) {
  if (!value || value === '-') return null;
  return (
    <div className="flex items-start gap-3 py-3 px-4 rounded-lg bg-primary-surface/50 hover:bg-primary-surface transition-colors">
      <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
        <Icon className="h-5 w-5 text-primary" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
        <p className="font-medium break-words mt-0.5">{value}</p>
      </div>
    </div>
  );
}

// Helper component for editable field
function EditableField({
  icon: Icon,
  label,
  value,
  isEditing,
  children,
}: {
  icon: React.ElementType;
  label: string;
  value: string | undefined | null;
  isEditing: boolean;
  children: React.ReactNode;
}) {
  if (isEditing) {
    return (
      <div className="space-y-2 p-4 rounded-lg bg-muted/30">
        <Label className="flex items-center gap-2 text-sm">
          <Icon className="h-4 w-4 text-primary" />
          {label}
        </Label>
        {children}
      </div>
    );
  }

  return <ProfileField icon={Icon} label={label} value={value} />;
}

export default function MyProfilePage() {
  const { user, refreshUser } = useAuth();
  const { t } = useTranslate();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [isCropperOpen, setIsCropperOpen] = useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [formData, setFormData] = useState<ProfileFormData>({
    description: '',
    zipCode: '',
    cityLabel: '',
    profession: '',
    spokenLanguages: [],
    lifePersonality: [],
  });

  const { data: nationalities = [] } = useQuery({
    queryKey: ['references', 'nationalities'],
    queryFn: referenceApi.listNationalities,
    staleTime: Number.POSITIVE_INFINITY,
  });
  const nationalityOptions = nationalities.map((n) => ({ value: String(n.id), label: n.label }));
  const nationalityLabel = (id: number | undefined): string =>
    nationalities.find((n) => n.id === id)?.label ?? '-';

  // Private profile (visible to others only after an accepted sharing)
  const privateFields: PrivateFieldDef[] = privateFieldsForGender(user?.gender);
  const [privateData, setPrivateData] = useState<PrivateFormData>({});
  const { data: privateRows = [] } = useQuery({
    queryKey: ['myPrivateProfile', user?.nodeId],
    queryFn: () => profileApi.getPrivateProfile(user?.nodeId as number),
    enabled: !!user?.nodeId,
  });
  // biome-ignore lint/correctness/useExhaustiveDependencies: privateFields derives from user.gender
  useEffect(() => {
    setPrivateData(fromPrivateFieldRows(privateFields, privateRows));
  }, [privateRows, user?.gender]);
  const privateValueLabel = (field: PrivateFieldDef): string => {
    const value = privateData[field.name];
    if (value === undefined || (Array.isArray(value) && value.length === 0) || value === '') {
      return t('profile.notSpecified');
    }
    if (field.kind === 'text') return String(value);
    const values = Array.isArray(value) ? value : [value];
    return values.map((v) => t(`profile.privateFields.${field.name}.values.${v}`)).join(', ');
  };

  // Fetch profile data
  const { data: profile, isLoading } = useQuery({
    queryKey: ['myProfile', user?.nodeId],
    queryFn: async () => {
      if (!user?.nodeId) throw new Error('No nodeId');
      return userApi.getMyProfile(user.nodeId);
    },
    enabled: !!user?.nodeId,
  });

  // Initialize form data when profile loads
  useEffect(() => {
    if (profile) {
      setFormData({
        description: profile.description || '',
        zipCode: profile.zip || '',
        geoId: profile.geoId,
        cityLabel: profile.city || '',
        profession: profile.profession || '',
        studyLevel: profile.studyLevel,
        nodeBusiness: profile.nodeBusiness,
        isStudyLevelMatter: profile.isStudyLevelMatter,
        maritalStatus: profile.maritalStatus,
        children: profile.children,
        livingPlace: profile.livingPlace,
        licence: profile.licence,
        smoke: profile.smoke,
        faith: profile.faith,
        minaj: profile.minaj,
        veil: profile.veil,
        familyValues: profile.familyValues,
        peerPast: profile.peerPast,
        administrativeState: profile.administrativeState,
        nationality: profile.nationality,
        origin: profile.origin,
        studyCountry: profile.studyCountry,
        spokenLanguages: profile.spokenLanguages?.split(',').filter(Boolean) || [],
        lifePersonality: profile.lifePersonality?.split(',').filter(Boolean) || [],
        shape: profile.shape,
        size: profile.size,
        eye: profile.eye,
        hair: profile.hair,
      });
    }
  }, [profile]);

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: ProfileFormData) => {
      return userApi
        .updateProfile({
          description: data.description || undefined,
          // only when a town was picked: re-sending the bare zip would move the
          // member to the first town of that zip
          geoId: data.geoId,
          profession: data.profession || undefined,
          avatarId: data.avatarId,
          studyLevel: data.studyLevel,
          nodeBusiness: data.nodeBusiness,
          isStudyLevelMatter: data.isStudyLevelMatter,
          maritalStatus: data.maritalStatus,
          children: data.children,
          livingPlace: data.livingPlace,
          licence: data.licence,
          smoke: data.smoke,
          faith: data.faith,
          minaj: data.minaj,
          veil: data.veil,
          familyValues: data.familyValues,
          peerPast: data.peerPast,
          administrativeState: data.administrativeState,
          nationality: data.nationality,
          origin: data.origin,
          studyCountry: data.studyCountry,
          spokenLanguages: data.spokenLanguages.join(',') || undefined,
          lifePersonality: data.lifePersonality.join(',') || undefined,
          shape: data.shape,
          size: data.size,
          eye: data.eye,
          hair: data.hair,
        })
        .then(async (updated) => {
          const payload = toPrivatePayload(privateFields, privateData);
          if (Object.keys(payload).length > 0) {
            await userApi.updatePrivateProfile(payload);
          }
          return updated;
        });
    },
    onSuccess: () => {
      toast.success(t('profile.saveChanges'));
      setIsEditing(false);
      queryClient.invalidateQueries({ queryKey: ['myProfile'] });
      queryClient.invalidateQueries({ queryKey: ['myPrivateProfile'] });
      refreshUser();
    },
    onError: () => {
      toast.error(t('common.error'));
    },
  });

  const handleEdit = () => setIsEditing(true);
  const handleSave = () => updateMutation.mutate(formData);
  const handleCancel = () => {
    setIsEditing(false);
    setPrivateData(fromPrivateFieldRows(privateFields, privateRows));
    if (profile) {
      setFormData({
        description: profile.description || '',
        zipCode: profile.zip || '',
        geoId: profile.geoId,
        cityLabel: profile.city || '',
        profession: profile.profession || '',
        studyLevel: profile.studyLevel,
        nodeBusiness: profile.nodeBusiness,
        isStudyLevelMatter: profile.isStudyLevelMatter,
        maritalStatus: profile.maritalStatus,
        children: profile.children,
        livingPlace: profile.livingPlace,
        licence: profile.licence,
        smoke: profile.smoke,
        faith: profile.faith,
        minaj: profile.minaj,
        veil: profile.veil,
        familyValues: profile.familyValues,
        peerPast: profile.peerPast,
        administrativeState: profile.administrativeState,
        nationality: profile.nationality,
        origin: profile.origin,
        studyCountry: profile.studyCountry,
        spokenLanguages: profile.spokenLanguages?.split(',').filter(Boolean) || [],
        lifePersonality: profile.lifePersonality?.split(',').filter(Boolean) || [],
        shape: profile.shape,
        size: profile.size,
        eye: profile.eye,
        hair: profile.hair,
      });
    }
  };

  const updateField = <K extends keyof ProfileFormData>(field: K, value: ProfileFormData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handlePhotoUpload = async (croppedBlob: Blob) => {
    setIsUploadingPhoto(true);
    try {
      await userApi.uploadPhoto(croppedBlob);
      toast.success(t('profile.photoUploaded', 'Photo mise à jour avec succès'));
      setIsCropperOpen(false);
      queryClient.invalidateQueries({ queryKey: ['myProfile'] });
    } catch {
      toast.error(t('profile.photoUploadError', 'Erreur lors de la mise à jour de la photo'));
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const toggleMultiSelect = (field: 'spokenLanguages' | 'lifePersonality', value: string) => {
    setFormData((prev) => {
      const current = prev[field];
      if (current.includes(value)) {
        return { ...prev, [field]: current.filter((v) => v !== value) };
      }
      return { ...prev, [field]: [...current, value] };
    });
  };

  // Helper to get translated value
  const getLabel = (category: string, value: number | undefined): string => {
    if (value === undefined || value === null) return '-';
    const translated = t(`profile.${category}.${value}`);
    return translated !== `profile.${category}.${value}` ? translated : '-';
  };

  const getBooleanLabel = (value: number | undefined): string => {
    if (value === undefined || value === null) return '-';
    return t(`profile.boolean.${value}`, { defaultValue: '-' });
  };

  // Format last activity date
  const formatLastActivity = (dateStr: string | undefined) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  // Render select in edit mode
  const renderSelect = (
    field: keyof ProfileFormData,
    options: { value: string; label: string }[],
  ) => {
    const value = formData[field as keyof ProfileFormData];
    const displayValue = value !== undefined && value !== null ? String(value) : undefined;

    return (
      <Select
        value={displayValue}
        onValueChange={(v) =>
          updateField(field, v === '' ? undefined : (Number.parseInt(v) as never))
        }
      >
        <SelectTrigger>
          <SelectValue placeholder={t('signup.placeholders.select')} />
        </SelectTrigger>
        <SelectContent>
          {options.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <Skeleton className="h-9 w-48 mb-2" />
            <Skeleton className="h-5 w-64" />
          </div>
          <Skeleton className="h-11 w-32" />
        </div>
        <div className="grid gap-6 lg:grid-cols-3">
          <Card className="lg:col-span-1">
            <CardContent className="p-0">
              <Skeleton className="aspect-[4/5] w-full" />
              <div className="p-6 space-y-4">
                <Skeleton className="h-6 w-3/4 mx-auto" />
                <Skeleton className="h-4 w-1/2 mx-auto" />
              </div>
            </CardContent>
          </Card>
          <div className="lg:col-span-2 space-y-6">
            {[1, 2, 3].map((i) => (
              <Card key={i}>
                <CardHeader>
                  <Skeleton className="h-6 w-32" />
                </CardHeader>
                <CardContent className="grid gap-3 sm:grid-cols-2">
                  <Skeleton className="h-20 w-full" />
                  <Skeleton className="h-20 w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">
            {t('profile.title')}
          </h1>
          <p className="text-muted-foreground mt-1">{t('profile.manage')}</p>
        </div>
        {!isEditing ? (
          <Button onClick={handleEdit} size="lg" className="gap-2">
            <Pencil className="h-4 w-4" />
            {t('profile.edit')}
          </Button>
        ) : (
          <div className="flex gap-3">
            <Button
              onClick={handleSave}
              size="lg"
              disabled={updateMutation.isPending}
              className="gap-2"
            >
              {updateMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {t('profile.saveChanges')}
            </Button>
            <Button variant="outline" size="lg" onClick={handleCancel} className="gap-2">
              <X className="h-4 w-4" />
              {t('profile.cancel')}
            </Button>
          </div>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Profile Photo & Info Card */}
        <Card className="lg:col-span-1 overflow-hidden">
          <CardContent className="p-0">
            {/* Photo section */}
            <div className="relative aspect-[4/5] bg-primary-surface">
              <Avatar className="h-full w-full rounded-none">
                <AvatarImage
                  src={getStorageUrl(profile?.avatarPath)}
                  alt={profile?.username}
                  className="object-cover"
                />
                <AvatarFallback className="text-7xl rounded-none bg-gradient-to-br from-primary to-primary-dark text-primary-foreground">
                  {profile?.username?.charAt(0).toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>

              {/* Star rating */}
              {profile && Number(profile.note) > 0 && (
                <div className="absolute top-3 left-3 flex bg-white/90 backdrop-blur-sm rounded-lg px-2 py-1 shadow-sm">
                  {Array.from({ length: Number(profile.note) }).map((_, i) => (
                    <Star key={i} className="h-4 w-4 text-accent fill-accent" />
                  ))}
                </div>
              )}

              {/* Photo verified badge */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
                <Badge
                  className={`${
                    profile?.isPhotoVerified
                      ? 'bg-success/90 hover:bg-success'
                      : 'bg-destructive/90 hover:bg-destructive'
                  } border-0`}
                >
                  {profile?.isPhotoVerified ? (
                    <>
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      {t('profile.photoVerified')}
                    </>
                  ) : (
                    <>
                      <X className="h-3 w-3 mr-1" />
                      {t('profile.photoNotVerified')}
                    </>
                  )}
                </Badge>
              </div>

              {/* Photo change is immediate (crop + upload), independent of the edit
                  mode — like the legacy photocrop route. Always reachable, since
                  the hint below says "click the camera icon". */}
              <Button
                size="icon"
                className="absolute top-3 right-3 rounded-full h-12 w-12 shadow-lg"
                onClick={() => setIsCropperOpen(true)}
                aria-label={t('profile.updatePhoto')}
                data-testid="profile-photo-button"
              >
                <Camera className="h-5 w-5" />
              </Button>
            </div>

            {/* Profile info */}
            <div className="p-6">
              <div className="text-center">
                {/* The username is fixed at signup (legacy: removed from every profile form) */}
                <h1 className="font-display text-2xl font-semibold">{profile?.username}</h1>
                <p className="text-muted-foreground mt-1">
                  {Number(profile?.age) > 0 && `${profile?.age} ans`}
                </p>
                {profile?.city && (
                  <div className="flex items-center justify-center mt-2 text-muted-foreground">
                    <MapPin className="h-4 w-4 mr-1 text-primary" />
                    {profile.zip && `${profile.zip} `}
                    {profile.city}
                    {profile.region && ` (${profile.region})`}
                  </div>
                )}
                {profile?.country && (
                  <div className="flex items-center justify-center mt-1 text-muted-foreground text-sm">
                    <Globe className="h-4 w-4 mr-1 text-primary" />
                    {profile.country}
                  </div>
                )}
                {user?.phone && (
                  <div
                    className="flex items-center justify-center mt-1 text-muted-foreground text-sm"
                    data-testid="profile-phone"
                  >
                    <Phone className="h-4 w-4 mr-1 text-primary" />
                    {user.phone}
                  </div>
                )}

                {/* Nationality & Origin flags */}
                {profile && (Number(profile.nationality) > 0 || Number(profile.origin) > 0) && (
                  <div className="flex items-center justify-center gap-3 mt-4 flex-wrap">
                    {Number(profile.nationality) > 0 && (
                      <div className="flex items-center gap-1.5 text-sm bg-primary-surface px-3 py-1.5 rounded-full">
                        <img
                          src={`/flags/${profile.nationality}.png`}
                          alt={nationalityLabel(profile.nationality)}
                          width={20}
                          height={14}
                          className="inline-block rounded-sm"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                        <span className="text-xs font-medium">
                          {nationalityLabel(profile.nationality)}
                        </span>
                      </div>
                    )}
                    {Number(profile.origin) > 0 && profile.origin !== profile.nationality && (
                      <div className="flex items-center gap-1.5 text-sm bg-accent/10 px-3 py-1.5 rounded-full">
                        <img
                          src={`/flags/${profile.origin}.png`}
                          alt={nationalityLabel(profile.origin)}
                          width={20}
                          height={14}
                          className="inline-block rounded-sm"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                        <span className="text-xs font-medium">
                          {nationalityLabel(profile.origin)}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Last activity */}
                {profile?.lastActivity && (
                  <div className="flex items-center justify-center mt-4 text-sm text-muted-foreground">
                    <Clock className="h-4 w-4 mr-1.5" />
                    {t('profile.lastConnection')}: {formatLastActivity(profile.lastActivity)}
                  </div>
                )}
              </div>

              <p className="mt-6 text-sm text-muted-foreground text-center">
                {t('profile.updatePhoto')}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Profile Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Public profile: everything below is visible to every member */}
          <div
            className="flex items-start gap-3 rounded-lg border border-primary/30 bg-primary/5 px-4 py-3"
            data-testid="visibility-public"
          >
            <Globe className="h-5 w-5 text-primary mt-0.5" />
            <div>
              <p className="font-display font-semibold">{t('profile.visibility.public')}</p>
              <p className="text-sm text-muted-foreground">{t('profile.visibility.publicHint')}</p>
            </div>
          </div>

          {/* Description */}
          <Card>
            <CardHeader>
              <CardTitle className="font-display flex items-center gap-2">
                <User className="h-5 w-5 text-primary" />
                {t('profile.sections.description')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isEditing ? (
                <Textarea
                  rows={4}
                  value={formData.description}
                  onChange={(e) => updateField('description', e.target.value)}
                  placeholder={t('signup.placeholders.description')}
                  className="resize-none"
                />
              ) : profile?.description ? (
                <p className="whitespace-pre-wrap leading-relaxed">{profile.description}</p>
              ) : (
                <p className="text-muted-foreground italic">{t('profile.noBio')}</p>
              )}
            </CardContent>
          </Card>

          {/* Location (legacy: zipcode + town, country derives from the town) */}
          <Card data-testid="profile-location">
            <CardHeader>
              <CardTitle className="font-display flex items-center gap-2">
                <MapPin className="h-5 w-5 text-primary" />
                {t('profile.sections.location')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isEditing ? (
                <ZipCityPicker
                  value={{
                    zipCode: formData.zipCode,
                    geoId: formData.geoId,
                    cityLabel: formData.cityLabel,
                  }}
                  onChange={(v) => setFormData((prev) => ({ ...prev, ...v }))}
                  idPrefix="profile-"
                />
              ) : (
                <div className="grid gap-3 sm:grid-cols-2">
                  <ProfileField
                    icon={MapPin}
                    label={t('profile.fields.city')}
                    value={
                      profile?.city
                        ? `${profile.zip ? `${profile.zip} ` : ''}${profile.city}${profile.region ? ` (${profile.region})` : ''}`
                        : undefined
                    }
                  />
                  <ProfileField
                    icon={Globe}
                    label={t('profile.fields.country')}
                    value={profile?.country}
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Studies and Profession */}
          <Card>
            <CardHeader>
              <CardTitle className="font-display flex items-center gap-2">
                <GraduationCap className="h-5 w-5 text-primary" />
                {t('profile.sections.studies')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 sm:grid-cols-2">
                <EditableField
                  icon={Briefcase}
                  label={t('profile.fields.profession')}
                  value={profile?.profession}
                  isEditing={isEditing}
                >
                  <Input
                    value={formData.profession}
                    onChange={(e) => updateField('profession', e.target.value)}
                    placeholder={t('signup.placeholders.profession')}
                  />
                </EditableField>
                <EditableField
                  icon={GraduationCap}
                  label={t('profile.fields.studyLevel')}
                  value={getLabel('studyLevel', profile?.studyLevel)}
                  isEditing={isEditing}
                >
                  {renderSelect('studyLevel', getFieldOptions(t, 'studyLevel'))}
                </EditableField>
                <EditableField
                  icon={Briefcase}
                  label={t('profile.fields.nodeBusiness')}
                  value={getLabel('nodeBusiness', profile?.nodeBusiness)}
                  isEditing={isEditing}
                >
                  {renderSelect('nodeBusiness', getFieldOptions(t, 'nodeBusiness'))}
                </EditableField>
                <EditableField
                  icon={GraduationCap}
                  label={t('profile.fields.isStudyLevelMatter')}
                  value={getBooleanLabel(profile?.isStudyLevelMatter)}
                  isEditing={isEditing}
                >
                  {renderSelect('isStudyLevelMatter', [
                    { value: '0', label: t('profile.boolean.0') },
                    { value: '1', label: t('profile.boolean.1') },
                  ])}
                </EditableField>
              </div>
            </CardContent>
          </Card>

          {/* Personal Situation */}
          <Card>
            <CardHeader>
              <CardTitle className="font-display flex items-center gap-2">
                <Heart className="h-5 w-5 text-primary" />
                {t('profile.sections.situation')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 sm:grid-cols-2">
                <EditableField
                  icon={Heart}
                  label={t('profile.fields.maritalStatus')}
                  value={getLabel('maritalStatus', profile?.maritalStatus)}
                  isEditing={isEditing}
                >
                  {renderSelect('maritalStatus', getFieldOptions(t, 'maritalStatus'))}
                </EditableField>
                <EditableField
                  icon={Baby}
                  label={t('profile.fields.children')}
                  value={getLabel('children', profile?.children)}
                  isEditing={isEditing}
                >
                  {renderSelect('children', getFieldOptions(t, 'children'))}
                </EditableField>
                <EditableField
                  icon={Home}
                  label={t('profile.fields.livingPlace')}
                  value={getLabel('livingPlace', profile?.livingPlace)}
                  isEditing={isEditing}
                >
                  {renderSelect('livingPlace', getFieldOptions(t, 'livingPlace'))}
                </EditableField>
                <EditableField
                  icon={User}
                  label={t('profile.fields.administrativeState')}
                  value={getLabel('administrativeState', profile?.administrativeState)}
                  isEditing={isEditing}
                >
                  {renderSelect('administrativeState', getFieldOptions(t, 'administrativeState'))}
                </EditableField>
                <EditableField
                  icon={Users}
                  label={t('profile.fields.familyValues')}
                  value={getLabel('familyValues', profile?.familyValues)}
                  isEditing={isEditing}
                >
                  {renderSelect('familyValues', getFieldOptions(t, 'familyValues'))}
                </EditableField>
                <EditableField
                  icon={Heart}
                  label={t('profile.fields.peerPast')}
                  value={getLabel('peerPast', profile?.peerPast)}
                  isEditing={isEditing}
                >
                  {renderSelect('peerPast', getFieldOptions(t, 'peerPast'))}
                </EditableField>
                <EditableField
                  icon={MapPin}
                  label={t('profile.fields.nationality')}
                  value={nationalityLabel(profile?.nationality)}
                  isEditing={isEditing}
                >
                  {renderSelect('nationality', nationalityOptions)}
                </EditableField>
                <EditableField
                  icon={MapPin}
                  label={t('profile.fields.origin')}
                  value={nationalityLabel(profile?.origin)}
                  isEditing={isEditing}
                >
                  {renderSelect('origin', nationalityOptions)}
                </EditableField>
              </div>
            </CardContent>
          </Card>

          {/* Religious Practice */}
          <Card>
            <CardHeader>
              <CardTitle className="font-display flex items-center gap-2">
                <Moon className="h-5 w-5 text-primary" />
                {t('profile.sections.religion')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 sm:grid-cols-2">
                <EditableField
                  icon={Moon}
                  label={t('profile.fields.faith')}
                  value={getLabel('faith', profile?.faith)}
                  isEditing={isEditing}
                >
                  {renderSelect('faith', getFieldOptions(t, 'faith'))}
                </EditableField>
                <EditableField
                  icon={Moon}
                  label={t('profile.fields.minaj')}
                  value={getLabel('minaj', profile?.minaj)}
                  isEditing={isEditing}
                >
                  {renderSelect('minaj', getFieldOptions(t, 'minaj'))}
                </EditableField>
                {user?.gender === 2 && (
                  <EditableField
                    icon={User}
                    label={t('profile.fields.veil')}
                    value={getLabel('veil', profile?.veil)}
                    isEditing={isEditing}
                  >
                    {renderSelect('veil', getFieldOptions(t, 'veil'))}
                  </EditableField>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Appearance */}
          <Card>
            <CardHeader>
              <CardTitle className="font-display flex items-center gap-2">
                <Eye className="h-5 w-5 text-primary" />
                {t('profile.sections.appearance')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 sm:grid-cols-2">
                <EditableField
                  icon={Palette}
                  label={t('profile.fields.shape')}
                  value={getLabel('shape', profile?.shape)}
                  isEditing={isEditing}
                >
                  {renderSelect('shape', getFieldOptions(t, 'shape'))}
                </EditableField>
                <EditableField
                  icon={Ruler}
                  label={t('profile.fields.size')}
                  value={profile?.size ? `${profile.size} cm` : '-'}
                  isEditing={isEditing}
                >
                  <Input
                    type="number"
                    min={SIZE_MIN}
                    max={SIZE_MAX}
                    value={formData.size || ''}
                    onChange={(e) =>
                      updateField(
                        'size',
                        e.target.value ? Number.parseInt(e.target.value) : undefined,
                      )
                    }
                    placeholder="170"
                  />
                </EditableField>
                <EditableField
                  icon={Eye}
                  label={t('profile.fields.eye')}
                  value={getLabel('eye', profile?.eye)}
                  isEditing={isEditing}
                >
                  {renderSelect('eye', getFieldOptions(t, 'eye'))}
                </EditableField>
                <EditableField
                  icon={Scissors}
                  label={t('profile.fields.hair')}
                  value={getLabel('hair', profile?.hair)}
                  isEditing={isEditing}
                >
                  {renderSelect('hair', getFieldOptions(t, 'hair'))}
                </EditableField>
              </div>
            </CardContent>
          </Card>

          {/* Other Info */}
          <Card>
            <CardHeader>
              <CardTitle className="font-display flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-primary" />
                {t('profile.about')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 sm:grid-cols-2">
                <EditableField
                  icon={Car}
                  label={t('profile.fields.licence')}
                  value={getBooleanLabel(profile?.licence)}
                  isEditing={isEditing}
                >
                  {renderSelect('licence', [
                    { value: '0', label: t('profile.boolean.0') },
                    { value: '1', label: t('profile.boolean.1') },
                  ])}
                </EditableField>
                <EditableField
                  icon={Cigarette}
                  label={t('profile.fields.smoke')}
                  value={getLabel('smoke', profile?.smoke)}
                  isEditing={isEditing}
                >
                  {renderSelect('smoke', getFieldOptions(t, 'smoke'))}
                </EditableField>
              </div>
            </CardContent>
          </Card>

          {/* Languages & Personality */}
          <Card>
            <CardHeader>
              <CardTitle className="font-display flex items-center gap-2">
                <Languages className="h-5 w-5 text-primary" />
                {t('profile.sections.languages')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Spoken Languages */}
                <div>
                  <p className="text-sm text-muted-foreground mb-3 uppercase tracking-wide">
                    {t('profile.fields.spokenLanguages')}
                  </p>
                  {isEditing ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {getFieldOptions(t, 'spokenLanguages').map((opt) => (
                        <div
                          key={opt.value}
                          className="flex items-center space-x-2 p-2 rounded-lg bg-muted/30"
                        >
                          <Checkbox
                            id={`lang-${opt.value}`}
                            checked={formData.spokenLanguages.includes(opt.value)}
                            onCheckedChange={() => toggleMultiSelect('spokenLanguages', opt.value)}
                          />
                          <label htmlFor={`lang-${opt.value}`} className="text-sm cursor-pointer">
                            {opt.label}
                          </label>
                        </div>
                      ))}
                    </div>
                  ) : profile?.spokenLanguages ? (
                    <div className="flex flex-wrap gap-2">
                      {profile.spokenLanguages.split(',').map((langId) => (
                        <Badge
                          key={langId}
                          className="bg-primary-surface text-primary border-0 px-3 py-1"
                        >
                          {t(`profile.spokenLanguages.${langId.trim()}`, { defaultValue: langId })}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground italic">-</p>
                  )}
                </div>

                {/* Life Personality */}
                <div>
                  <p className="text-sm text-muted-foreground mb-3 uppercase tracking-wide">
                    {t('profile.fields.lifePersonality')}
                  </p>
                  {isEditing ? (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {getFieldOptions(t, 'lifePersonality').map((opt) => (
                        <div
                          key={opt.value}
                          className="flex items-center space-x-2 p-2 rounded-lg bg-muted/30"
                        >
                          <Checkbox
                            id={`personality-${opt.value}`}
                            checked={formData.lifePersonality.includes(opt.value)}
                            onCheckedChange={() => toggleMultiSelect('lifePersonality', opt.value)}
                          />
                          <label
                            htmlFor={`personality-${opt.value}`}
                            className="text-sm cursor-pointer"
                          >
                            {opt.label}
                          </label>
                        </div>
                      ))}
                    </div>
                  ) : profile?.lifePersonality ? (
                    <div className="flex flex-wrap gap-2">
                      {profile.lifePersonality.split(',').map((traitId) => (
                        <Badge
                          key={traitId}
                          className="bg-accent/10 text-accent border-0 px-3 py-1"
                        >
                          {t(`profile.lifePersonality.${traitId.trim()}`, {
                            defaultValue: traitId,
                          })}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground italic">-</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Private profile: shared only after an accepted sharing request */}
          <div
            className="flex items-start gap-3 rounded-lg border border-success/40 bg-success/5 px-4 py-3"
            data-testid="visibility-private"
          >
            <Lock className="h-5 w-5 text-success mt-0.5" />
            <div>
              <p className="font-display font-semibold">{t('profile.visibility.private')}</p>
              <p className="text-sm text-muted-foreground">{t('profile.visibility.privateHint')}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {t('profile.visibility.privateIntro')}
              </p>
            </div>
          </div>

          <Card className="border-success/20">
            <CardHeader>
              <CardTitle className="font-display flex items-center gap-2">
                <Lock className="h-5 w-5 text-success" />
                {t('profile.sections.private')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 sm:grid-cols-2">
                {privateFields.map((field) => {
                  const label = t(`profile.privateFields.${field.name}.label`);
                  const value = privateData[field.name];
                  if (!isEditing) {
                    return (
                      <ProfileField
                        key={field.name}
                        icon={Lock}
                        label={label}
                        value={privateValueLabel(field)}
                      />
                    );
                  }
                  if (field.kind === 'text') {
                    return (
                      <div
                        key={field.name}
                        className="space-y-2 p-4 rounded-lg bg-muted/30 sm:col-span-2"
                      >
                        <Label className="text-sm">{label}</Label>
                        <Textarea
                          rows={3}
                          maxLength={2000}
                          value={typeof value === 'string' ? value : ''}
                          onChange={(e) =>
                            setPrivateData((prev) => ({ ...prev, [field.name]: e.target.value }))
                          }
                        />
                      </div>
                    );
                  }
                  const options = getPrivateFieldOptions(t, field);
                  if (field.kind === 'multi') {
                    const values = Array.isArray(value) ? value : [];
                    return (
                      <div
                        key={field.name}
                        className="space-y-2 p-4 rounded-lg bg-muted/30 sm:col-span-2"
                      >
                        <Label className="text-sm">{label}</Label>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {options.map((opt) => (
                            <div key={opt.value} className="flex items-center space-x-2">
                              <Checkbox
                                id={`private-${field.name}-${opt.value}`}
                                checked={values.includes(opt.value)}
                                onCheckedChange={() =>
                                  setPrivateData((prev) => ({
                                    ...prev,
                                    [field.name]: values.includes(opt.value)
                                      ? values.filter((v) => v !== opt.value)
                                      : [...values, opt.value],
                                  }))
                                }
                              />
                              <label
                                htmlFor={`private-${field.name}-${opt.value}`}
                                className="text-sm cursor-pointer"
                              >
                                {opt.label}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div key={field.name} className="space-y-2 p-4 rounded-lg bg-muted/30">
                      <Label className="text-sm">{label}</Label>
                      <Select
                        value={typeof value === 'string' && value !== '' ? value : undefined}
                        onValueChange={(v) =>
                          setPrivateData((prev) => ({ ...prev, [field.name]: v }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={t('signup.placeholders.select')} />
                        </SelectTrigger>
                        <SelectContent>
                          {options.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Image Cropper Dialog */}
      <ImageCropper
        open={isCropperOpen}
        onClose={() => setIsCropperOpen(false)}
        onCropComplete={handlePhotoUpload}
        aspectRatio={3 / 4}
        title={t('profile.updatePhoto', 'Modifier votre photo')}
        isUploading={isUploadingPhoto}
      />
    </div>
  );
}
